import LoyaltyIcon from 'components/ReusableComponents/Arrivals/LoyaltyIcon'
import { getLoyaltyLevelText } from 'components/ReusableComponents/Arrivals/LoyaltyIcon/helpers'
import EditGuestDialog from 'components/ReusableComponents/EditGuestDialog'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import EditIcon from 'components/ReusableComponents/Icons/Edit'
import PopupIcon from 'components/ReusableComponents/Icons/Popup'
import MobileLink from 'components/ReusableComponents/MobileLink'
import { useUnlockService } from 'components/ReusableComponents/UnlockServiceDialog'
import BookingHistory from 'features/BookingDetails/BookingHistory'
import BookingDetailsDialog from 'features/BookingDetailsDialog'
import AvailabilitySearchForm from 'features/Messaging/GuestInfoSidebar/AvailabilitySearchForm'
import { subscriptionStatusTypes } from 'features/Messaging/messaging-helpers'
import { useInternalAdmin } from 'hooks'
import useCopyText from 'hooks/useCopyText'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import subscribersApi from 'services/api/subscribers'
import { formatPhoneNumber } from 'utils/helpers'

import { ReactComponent as ConversationIcon } from './conversationIcon.svg'
import { ReactComponent as GuestIcon } from './guestIcon.svg'
import styles from './GuestInfoSidebar.module.scss'
import { ReactComponent as ReservationIcon } from './reservationIcon.svg'

const Row = ({ label, value }) => (
  <div className={styles.row}>
    <div className={styles.rowLabel}>{label}</div>
    <div className={styles.rowValue}>{value || 'Unknown'}</div>
  </div>
)

const Group = ({ children, title, icon, isEditable = false, onEditClick }) => (
  <div className={styles.group}>
    <div className={styles.groupTitle}>
      {icon}
      <span className={styles.groupTitleText}>{title}</span>
      {isEditable && (
        <button className={styles.editButton} onClick={onEditClick}>
          <EditIcon /> Edit
        </button>
      )}
    </div>
    {children}
  </div>
)

const GuestInfoSidebar = ({
  roomNo = 'Unknown',
  roomCode = 'Unknown',
  checkIn,
  checkOut,
  subsidiaryPhone,
  confirmationUrl,
  subscriberId,
  bookingId,
  chatId,
  refreshChat,
  subscriberUtcOffSet,
  messagingType
}) => {
  const [guestInfo, setGuestInfo] = useState({})
  const [guestInfoState, setGuestInfoState] = useState('pending')

  const [showBookingDetails, setShowBookingDetails] = useState(false)
  const [showBookingHistory, setShowBookingHistory] = useState(false)

  const isInternalAdmin = useInternalAdmin()

  const copy = useCopyText()

  useEffect(() => {
    if (subscriberId) {
      subscribersApi
        .find({ subscriberId })
        .then(results => {
          setGuestInfo(results.subscribersFound[subscriberId])

          setGuestInfoState('fullfilled')
        })
        .catch(() => {
          setGuestInfoState('rejected')
        })
    }
  }, [subscriberId])

  function formatDate(date, withHours) {
    return date
      ? moment(date).format(withHours ? 'MMM D, h:MM A' : 'MMM D, YYYY')
      : 'Unknown'
  }

  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)

  const {
    fullName,
    loyaltyLevel,
    loyaltyProgram,
    loyaltyId,
    phone,
    subscriptionStatus,
    state,
    city,
    email,
    lastCheckOut,
    statusChangedOn
  } = guestInfo

  const unknownDate = '1978-09-12T00:00:00'

  const loyaltyLevelText = getLoyaltyLevelText(parentChainCode, loyaltyLevel)

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const { hasMessagingService, hasMarketingService } = useUnlockService()
  const subscriptionStatusType = subscriptionStatusTypes[subscriptionStatus]
  if (guestInfoState === 'pending') return <div className={styles.wrapper}></div>

  if (messagingType === 'sales' && !hasMarketingService) {
    return null
  }

  if (messagingType === 'service' && !hasMessagingService) {
    return null
  }

  if (messagingType === 'direct' && !hasMessagingService) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      {isEditDialogOpen && (
        <EditGuestDialog
          currentFirstName={fullName.split(' ')[0]}
          currentLastName={fullName.split(' ')[fullName.split(' ').length - 1]}
          currentEmail={email}
          chatId={chatId}
          onClose={() => setIsEditDialogOpen(false)}
          refreshChat={refreshChat}
        />
      )}
      {showBookingDetails && (
        <BookingDetailsDialog
          bookingId={bookingId}
          onClose={() => setShowBookingDetails(false)}
        />
      )}
      {showBookingHistory && (
        <BookingHistory
          subscriberId={subscriberId}
          onClose={() => setShowBookingHistory(false)}
        />
      )}
      {guestInfoState === 'fullfilled' && (
        <Group
          title={fullName}
          isEditable
          onEditClick={() => setIsEditDialogOpen(true)}
          icon={
            loyaltyProgram ? (
              <LoyaltyIcon loyaltyProgram={loyaltyProgram} loyaltyLevel={loyaltyLevel} />
            ) : (
              <GuestIcon />
            )
          }
        >
          <Row label="Phone" value={formatPhoneNumber(phone)} />
          <Row label="Email" value={email} />
          {loyaltyLevelText && <Row label="Loyalty level" value={loyaltyLevelText} />}
          {loyaltyId && <Row label="Loyalty id" value={loyaltyId} />}
          {city && state && (
            <Row
              label="City &amp; state"
              value={`${city ? `${city}` : ''}${state ? `, ${state}` : ''}`}
            />
          )}
          {subscriberUtcOffSet && (
            <Row
              label="Local time"
              value={moment().utcOffset(subscriberUtcOffSet).format('MMM D, h:mm A')}
            />
          )}
          <Row
            label="Last checkout"
            value={
              lastCheckOut === unknownDate
                ? 'Unknown'
                : moment(lastCheckOut).format('MMM DD, YYYY')
            }
          />
          {subscriptionStatusType && (
            <Row
              label={subscriptionStatusType}
              value={formatDate(statusChangedOn, true)}
            />
          )}

          <button
            className={styles.reservationDetails}
            onClick={() => setShowBookingHistory(true)}
          >
            Guest profile →
          </button>
        </Group>
      )}
      <Group title="Reservation" icon={<ReservationIcon />}>
        <Row label="Check-in" value={formatDate(checkIn)} />
        <Row label="Check-out" value={formatDate(checkOut)} />
        <Row label="Room no" value={roomNo} />
        <Row label="Room code" value={roomCode} />
        {isInternalAdmin && confirmationUrl && (
          <Row
            label="Conf. link"
            value={
              <div className={styles.confirmationLinkButtons}>
                <MobileLink href={confirmationUrl} class>
                  <PopupIcon width={18} height={18} />
                </MobileLink>
                <button>
                  <CopyIcon
                    width={16}
                    height={16}
                    onClick={() => copy(confirmationUrl)}
                  />
                </button>
              </div>
            }
          />
        )}
        <button
          className={styles.reservationDetails}
          onClick={() => setShowBookingDetails(true)}
        >
          See reservation details →
        </button>
      </Group>
      <Group title="Conversation" icon={<ConversationIcon />}>
        <Row label="Hotel phone" value={formatPhoneNumber(subsidiaryPhone)} />
      </Group>
      {isInternalAdmin && (
        <Group title="Availability &amp; pricing">
          <AvailabilitySearchForm chatId={chatId} subscriberId={subscriberId} />
        </Group>
      )}
    </div>
  )
}

export default GuestInfoSidebar
